import { useIntl } from "react-intl";
import { Box } from "@mui/material";
import { get } from "lodash-es";
import { useTheme } from "@mui/styles";
import { useOutletContext } from "react-router";
import { useCallback, useEffect, useState } from "react";
import MetaCRMTag from "@metacrm/metacrm-material-ui/dist/MetaCRMTag";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import { formatNumberWithSymbol } from "features/helpers/utils";

export default function useGetKolTable() {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const [entityName] = useOutletContext();

  const headers = [
    {
      title: "KOL Name",
      label: formatMessage({ id: "global.header.kolName" }),
      disabledSort: true,
      show: true,
      id: "kolName",
      width: 270,
    },
    {
      title: "X Followers",
      label: formatMessage({ id: "global.header.followers" }),
      disabledSort: false,
      show: true,
      id: "xFlollowers",
      width: 130,
    },
    {
      title: "Categories",
      label: formatMessage({ id: "global.header.categories" }),
      disabledSort: true,
      show: true,
      id: "categories",
    },
    {
      title: "ETR",
      label: (
        <Box display="flex" alignItems="center" gap="5px">
          {formatMessage({ id: "global.header.etr" })}{" "}
          <IconTooltip
            content={formatMessage({
              id: "global.header.etr.tooltip",
            })}
          />
        </Box>
      ),
      disabledSort: false,
      show: true,
      id: "etr",
    },
    {
      title: "VTR",
      label: (
        <Box display="flex" alignItems="center" gap="5px">
          {formatMessage({ id: "global.header.vtr" })}{" "}
          <IconTooltip
            content={formatMessage({
              id: "global.header.vtr.tooltip",
            })}
          />
        </Box>
      ),
      disabledSort: false,
      show: true,
      id: "vtr",
    },
    {
      title: "Original Tweets Ratio",
      label: (
        <Box display="flex" alignItems="center" gap="5px">
          {formatMessage({ id: "global.header.originalTweetsRatio" })}
          <IconTooltip
            content={formatMessage({
              id: "global.header.originalTweetsRatio.tooltip",
            })}
          />
        </Box>
      ),
      disabledSort: false,
      show: true,
      id: "originalTweetsRatio",
    },
    {
      title: "Language",
      label: formatMessage({ id: "global.header.language" }),
      disabledSort: false,
      show: true,
      id: "language",
    },
    {
      title: "Location",
      label: formatMessage({ id: "global.header.location" }),
      disabledSort: false,
      show: true,
      id: "location",
    },
  ];

  const renderContents = useCallback(
    (kol) => {
      return [
        <Box
          slot="kolName"
          onClick={(e) => e.stopPropagation()}
          display="flex"
          alignItems="center"
          gap="8px"
        >
          <img
            src={kol.avatar}
            style={{ width: "64px", borderRadius: "50%" }}
          />
          <Box>
            <Box
              fontWeight="bold"
              fontSize={14}
              color={theme.customColors.grey[900]}
              display="flex"
              alignItems="center"
              gap="5px"
            >
              {kol.name}
              {kol.verified && (
                <Box color="#5890FF" display="flex" alignItems="center">
                  <i className="meta-crm-icon-ic_blueCheck font-size-14" />
                </Box>
              )}
            </Box>
            <Box fontSize={12} color={theme.customColors.grey[700]}>
              {kol.description}
            </Box>
          </Box>
        </Box>,
        <Box slot="xFlollowers">
          {formatNumberWithSymbol(kol?.xFollowers) || "-"}
        </Box>,
        <Box slot="categories">
          {kol?.categories?.length > 0 ? (
            <Box display="flex" alignItems="center">
              {kol.categories.slice(0, 2).map((category) => (
                <MetaCRMTag
                  key={category.value}
                  option={{
                    color: get(category, "color", "#E6E6E6"),
                    label: category.label,
                  }}
                />
              ))}
              {kol.categories.length > 2 && (
                <Box color="#7b61ff" fontSize={12} ml="4px">
                  +{kol.categories.length - 2}
                </Box>
              )}
            </Box>
          ) : (
            "-"
          )}
        </Box>,
        <Box slot="etr">{kol?.etr || "-"}%</Box>,
        <Box slot="vtr">{kol?.vtr || "-"}%</Box>,
        <Box slot="originalTweetsRatio">
          {kol?.originalTweetsRatio || "-"}%
        </Box>,
        <Box slot="language">
          {kol?.language?.length > 0 ? (
            <Box display="flex" alignItems="center">
              {kol.language.slice(0, 2).map((l) => (
                <MetaCRMTag
                  key={l.value}
                  option={{ color: get(l, "color", "#E6E6E6"), label: l.label }}
                />
              ))}
              {kol.language.length > 2 && (
                <Box color="#7b61ff" fontSize={12} ml="4px">
                  +{kol.language.length - 2}
                </Box>
              )}
            </Box>
          ) : (
            "-"
          )}
        </Box>,
        <Box slot="location">
          {kol?.location?.length > 0 ? (
            <Box display="flex" alignItems="center">
              {kol.location.slice(0, 2).map((l) => (
                <MetaCRMTag
                  key={l.value}
                  option={{ color: get(l, "color", "#E6E6E6"), label: l.label }}
                />
              ))}
              {kol.location.length > 2 && (
                <Box color="#7b61ff" fontSize={12} ml="4px">
                  +{kol.location.length - 2}
                </Box>
              )}
            </Box>
          ) : (
            "-"
          )}
        </Box>,
      ];
    },
    [theme]
  );

  return {
    headers,
    renderContents,
  };
}
